/**
 * "Property Cash Flow" panel — mirrors the cash-flow section of
 * `design/Syntra/property-detail.html`: a year selector, YTD summary tiles, a
 * "Full year" context band, a "Selected month" band (with Add-expense), a phased
 * Jan-Dec month list, the selected month's section-grouped Item/Detail/Amount
 * table (opex collapses to a reserve line + subtotal, expandable), and a right
 * rail (net summary + outflow breakdown).
 *
 * Past months are frozen snapshots, the current month is live, future months
 * render as "—" (no projection).
 */
import { useState } from "react";
import { ArrowDown, ArrowUp, List, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/calculators";
import type {
  YearMonthWithLogged,
  YearSummary,
} from "@/components/properties/cashflow-detail";

interface PropertyCashflowPanelProps {
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  months: YearMonthWithLogged[];
  summary: YearSummary;
  /** Whether the property has a rented listing — labels operating months
   * "rented" vs "vacant". */
  isRented: boolean;
  onAddExpense: (month: YearMonthWithLogged) => void;
  onViewBreakdown: (month: YearMonthWithLogged) => void;
}

function netClass(n: number): string {
  if (Math.abs(n) < 0.5) return "text-muted-foreground";
  return n >= 0 ? "text-success" : "text-destructive";
}

function signed(n: number): string {
  return `${n >= 0 ? "+" : ""}${fmtMoney(n)}`;
}

/** Sum of the maintenance + capex reserve lines for a month. */
function maintCapexOf(month: YearMonthWithLogged): number {
  return (month.breakdown?.opex.lines ?? [])
    .filter((l) => l.key === "maint" || l.key === "capex")
    .reduce((s, l) => s + l.amount, 0);
}

export function PropertyCashflowPanel({
  year,
  years,
  onYearChange,
  months,
  summary,
  isRented,
  onAddExpense,
  onViewBreakdown,
}: PropertyCashflowPanelProps) {
  // Default selection: the current (live) month, else the last month with data.
  const defaultIdx = (() => {
    const live = months.findIndex((m) => m.source === "live");
    if (live >= 0) return live;
    const withData = months.map((m, i) => (m.breakdown ? i : -1)).filter((i) => i >= 0);
    return withData.length ? withData[withData.length - 1] : 0;
  })();
  const [selected, setSelected] = useState(defaultIdx);
  const active = months[Math.min(selected, months.length - 1)] ?? months[0];

  // Month-over-month delta vs the previous month that had figures.
  const prevWithData = (() => {
    for (let i = months.indexOf(active) - 1; i >= 0; i -= 1) {
      if (months[i]?.breakdown) return months[i];
    }
    return null;
  })();

  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Property Cash Flow</h2>
          <p className="text-xs text-muted-foreground">
            Monthly income, expenses, and one-time capital across the year
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => onYearChange(Number(v))}>
          <SelectTrigger className="h-9 w-[110px]" aria-label="Year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Full-year context band */}
      <Band pill={`Full year · ${year}`} text="Year-to-date totals across all months" />

      {/* YTD summary tiles */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <SummaryTile label="Rental income" value={fmtMoney(summary.income)} tone="success" sub="Collected YTD" />
        <SummaryTile
          label="Operating + debt"
          value={fmtMoney(summary.operatingAndDebt + summary.logged)}
          sub="Recurring costs YTD"
        />
        <SummaryTile label="Rehab (one-time)" value={fmtMoney(summary.rehab)} tone="warning" sub="Capital, not recurring" />
        <SummaryTile
          label="Net cash flow YTD"
          value={fmtMoney(summary.net)}
          tone={summary.net >= 0 ? "success" : "destructive"}
          sub={`${fmtMoney(summary.netExRehab)} ex-rehab`}
          emphasize
        />
      </div>

      {/* Selected-month context band + Add expense */}
      <div className="flex flex-wrap items-center gap-3">
        <Band
          tone="blue"
          pill={`Selected month · ${active.monthName} ${active.year}`}
          text="Income, expenses and debt for this month only"
        />
        {active.breakdown && (
          <Button size="sm" className="ml-auto" onClick={() => onAddExpense(active)}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add expense to {active.monthName}
          </Button>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[210px_1fr_290px]">
        <MonthList months={months} selected={selected} onSelect={setSelected} />

        {/* Selected month detail */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">
              {active.monthName} {active.year}
            </span>
            <PhaseBadge month={active} isRented={isRented} />
          </div>
          <MonthDetailTable month={active} />
          {active.breakdown && (
            <button
              type="button"
              onClick={() => onViewBreakdown(active)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-xs font-semibold text-primary transition-colors hover:bg-muted"
            >
              <List className="h-3.5 w-3.5" />
              View full breakdown
            </button>
          )}
        </div>

        {active.breakdown && <NetSummary month={active} prev={prevWithData} />}
      </div>
    </Card>
  );
}

/* ── Context band ───────────────────────────────────────────────────────── */
function Band({ pill, text, tone }: { pill: string; text: string; tone?: "blue" }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
      <span
        className={cn(
          "rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
          tone === "blue" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
        )}
      >
        {pill}
      </span>
      <span className="text-xs text-muted-foreground">{text}</span>
    </div>
  );
}

/* ── Month list ─────────────────────────────────────────────────────────── */
function MonthList({
  months,
  selected,
  onSelect,
}: {
  months: YearMonthWithLogged[];
  selected: number;
  onSelect: (i: number) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {months[0]?.year} by month
      </div>
      {months.map((m, i) => {
        const prev = i > 0 ? months[i - 1] : null;
        const delta =
          m.phase === "operating" && prev?.phase === "operating"
            ? m.netWithLogged - prev.netWithLogged
            : 0;
        return (
          <button
            key={m.month}
            type="button"
            onClick={() => onSelect(i)}
            className={cn(
              // The list sits in a fixed 210px track, so a wide month (amount
              // + month-over-month delta) has to wrap onto a second line
              // rather than run past the row's border.
              "flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-md border border-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-muted",
              i === selected && "border-syntra-red/50 bg-syntra-red/10",
            )}
          >
            <span
              className={cn(
                "shrink-0",
                m.breakdown
                  ? "font-semibold text-foreground"
                  : "font-medium text-muted-foreground",
              )}
            >
              {m.monthName.slice(0, 3)}
              {m.source === "live" && <span className="ml-1 text-[10px] text-muted-foreground">now</span>}
            </span>
            {m.phase === "rehab" && m.rehabSpend > 0 ? (
              <span className="ml-auto whitespace-nowrap rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning">
                Rehab {fmtMoney(m.netWithLogged)}
              </span>
            ) : m.breakdown ? (
              <span className="ml-auto flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
                <span
                  className={cn(
                    "whitespace-nowrap font-semibold tabular-nums",
                    netClass(m.netWithLogged),
                  )}
                >
                  {fmtMoney(m.netWithLogged)}
                </span>
                {Math.abs(delta) >= 0.5 && (
                  <span
                    className={cn(
                      "inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[10px] font-semibold tabular-nums",
                      delta > 0 ? "text-success" : "text-destructive",
                    )}
                  >
                    {delta > 0 ? (
                      <ArrowUp className="h-3 w-3 shrink-0" />
                    ) : (
                      <ArrowDown className="h-3 w-3 shrink-0" />
                    )}
                    {signed(delta)}
                  </span>
                )}
              </span>
            ) : (
              <span className="ml-auto text-muted-foreground">—</span>
            )}
          </button>
        );
      })}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <Legend dot="bg-success" label="Operating" />
        <Legend dot="bg-warning" label="Rehab" />
        <Legend dot="bg-muted-foreground" label="Pre-acq." />
      </div>
    </div>
  );
}

function Legend({ dot, label }: { dot: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("h-2 w-2 rounded-sm", dot)} /> {label}
    </span>
  );
}

/* ── Detail table (Item / Detail / Amount) ────────────────────────────────── */
function MonthDetailTable({ month }: { month: YearMonthWithLogged }) {
  if (!month.breakdown) {
    // Distinguish "not owned yet" from "owned, but no figures recorded". The
    // `pre` phase means the month falls before the property's owned_date, so it
    // genuinely wasn't owned; a rehab/operating month with no snapshot was
    // owned — there's just no data for it.
    const message =
      month.phase === "future"
        ? "This month hasn't happened yet."
        : month.phase === "sold"
          ? "This property was sold — no cash flow after the sale."
          : month.phase === "pre"
            ? "This property wasn't owned yet this month."
            : "No data is available for this month.";
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {message}
      </div>
    );
  }
  const b = month.breakdown;
  const isRehab = month.phase === "rehab";
  const maintCapex = maintCapexOf(month);

  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 border-b border-border px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span>Item</span>
        <span className="text-right">Detail</span>
        <span className="text-right">Amount</span>
      </div>

      {!isRehab && (
        <>
          <Section label="Income" />
          <Row item="Effective gross income" detail="—" amount={`+${fmtMoney(b.income.egi)}`} tone="success" />
        </>
      )}

      <Section label={isRehab ? "Holding costs" : "Operating expenses"} />
      {isRehab ? (
        <Row item="Total holding costs" detail="taxes · ins · utils" amount={`-${fmtMoney(b.opex.total)}`} tone="destructive" indent />
      ) : (
        <Row item="Maintenance & CapEx reserve" detail="reserve" amount={`-${fmtMoney(maintCapex)}`} tone="destructive" indent />
      )}
      {month.loggedTotal > 0 && (
        <Row item="Logged expenses" detail={`${month.logged.length} ${month.logged.length === 1 ? "entry" : "entries"}`} amount={`-${fmtMoney(month.loggedTotal)}`} tone="destructive" indent />
      )}
      <Row item="Subtotal" amount={`-${fmtMoney(b.opex.total + month.loggedTotal)}`} tone="destructive" subtotal />

      {isRehab && month.rehabSpend > 0 && (
        <>
          <Section label="Rehab" />
          <Row item="Total rehab spend" detail="one-time" amount={`-${fmtMoney(month.rehabSpend)}`} tone="warning" />
        </>
      )}

      <Section label="Debt service" />
      <Row item={isRehab ? "Loan interest carry" : "Monthly loan payment"} detail="—" amount={`-${fmtMoney(b.debt)}`} tone="destructive" />
      <Row item="Net cash flow" amount={signed(month.netWithLogged)} tone={month.netWithLogged >= 0 ? "success" : "destructive"} total />
    </div>
  );
}

function Section({ label }: { label: string }) {
  return (
    <div className="bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
  );
}

function Row({
  item,
  detail,
  amount,
  tone,
  indent,
  subtotal,
  total,
}: {
  item: string;
  detail?: string;
  amount: string;
  tone?: "success" | "destructive" | "warning";
  indent?: boolean;
  subtotal?: boolean;
  total?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border px-4 py-2.5 text-sm last:border-b-0",
        subtotal && "bg-muted/30",
        total && "border-t-2 border-border bg-muted/60 font-semibold",
      )}
    >
      <span className={cn("text-foreground", indent && "pl-3 text-muted-foreground")}>{item}</span>
      <span className="text-right text-xs text-muted-foreground">{detail ?? ""}</span>
      <span
        className={cn(
          "text-right font-semibold tabular-nums",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
          tone === "warning" && "text-warning",
        )}
      >
        {amount}
      </span>
    </div>
  );
}

/* ── Right rail: net summary + outflow breakdown ──────────────────────────── */
function NetSummary({
  month,
  prev,
}: {
  month: YearMonthWithLogged;
  prev: YearMonthWithLogged | null;
}) {
  const b = month.breakdown!;
  const delta = prev ? month.netWithLogged - prev.netWithLogged : 0;
  const maintCapex = maintCapexOf(month);
  const operatingOther = b.opex.total - maintCapex + month.loggedTotal;
  // Palette mirrors the "Where the money goes" section in the reference design
  // (design/Syntra/property-detail.html): violet / blue / amber / red — each
  // category a distinct hue so the bar and legend read as four separate items.
  const segments = [
    { key: "debt", label: "Debt service", amount: b.debt, dot: "bg-violet-500" },
    { key: "opex", label: "Operating expenses", amount: operatingOther, dot: "bg-blue-500" },
    { key: "maint", label: "Maintenance & CapEx", amount: maintCapex, dot: "bg-amber-500" },
    { key: "rehab", label: "Rehab capital", amount: month.rehabSpend, dot: "bg-red-500" },
  ].filter((s) => s.amount > 0);
  const outflow = segments.reduce((s, x) => s + x.amount, 0) || 1;

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          "rounded-xl border p-4",
          month.netWithLogged >= 0 ? "border-success/30" : "border-destructive/30",
        )}
      >
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Net cash flow
        </div>
        <div className={cn("mt-1 text-2xl font-bold tabular-nums", netClass(month.netWithLogged))}>
          {fmtMoney(month.netWithLogged)}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">EGI − operating expenses − debt</div>
        {prev && Math.abs(delta) >= 0.5 && (
          <div
            className={cn(
              "mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
              delta > 0 ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive",
            )}
          >
            {delta > 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
            {signed(delta)} vs {prev.monthName.slice(0, 3)}
          </div>
        )}
      </div>

      {outflow > 1 && (
        <div className="rounded-xl border border-border p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Where the money goes
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {month.monthName.slice(0, 3)} outflow · {fmtMoney(outflow)} total
          </div>
          <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-muted">
            {segments.map((s) => (
              <div key={s.key} className={cn("h-full", s.dot)} style={{ width: `${(s.amount / outflow) * 100}%` }} />
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-2">
            {segments.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-xs">
                <span className={cn("h-2.5 w-2.5 rounded-sm", s.dot)} />
                <span className="flex-1 text-foreground">{s.label}</span>
                <span className="text-muted-foreground">{Math.round((s.amount / outflow) * 100)}%</span>
                <span className="font-semibold tabular-nums text-foreground">{fmtMoney(s.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone,
  sub,
  emphasize,
}: {
  label: string;
  value: string;
  tone?: "success" | "destructive" | "warning";
  sub?: string;
  emphasize?: boolean;
}) {
  return (
    <div className={cn("rounded-lg border border-border bg-muted/40 p-3", emphasize && "border-primary/20 bg-primary/5")}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-1 text-lg font-bold tabular-nums text-foreground",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
          tone === "warning" && "text-warning",
        )}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function PhaseBadge({
  month,
  isRented,
}: {
  month: YearMonthWithLogged;
  isRented: boolean;
}) {
  if (month.phase === "operating")
    return (
      <Badge className="bg-success/15 text-success hover:bg-success/15">
        Operating · {isRented ? "rented" : "vacant"}
      </Badge>
    );
  if (month.phase === "future") return <Badge variant="secondary">Future</Badge>;
  if (month.phase === "sold") return <Badge variant="secondary">Sold</Badge>;
  if (month.phase === "pre") return <Badge variant="secondary">Not yet owned</Badge>;
  // Rehab phase: only badge it "Rehab · vacant" when rehab actually happened
  // this month (spend) or there are recorded figures. An owned rehab-phase
  // month with neither is just a vacant month with no data — don't imply rehab.
  const hasRehabActivity = month.rehabSpend > 0 || Boolean(month.breakdown);
  return hasRehabActivity ? (
    <Badge className="bg-warning/15 text-warning hover:bg-warning/15">Rehab · vacant</Badge>
  ) : (
    <Badge variant="secondary">Vacant</Badge>
  );
}
